import type {
  StorageStatus,
  DriveFileList,
  OneDriveItemList,
  ZohoItemList,
  UnifiedFileItem,
  StorageActivityLog,
  ClientFolderResult,
} from '../types/storage';

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

function getToken(): string {
  return localStorage.getItem('token') || '';
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ─── Status ───────────────────────────────────────────────────────────────────

export const storageApi = {
  getStatus: () => apiFetch<StorageStatus>('/storage/status'),
  getRecentFiles: () => apiFetch<UnifiedFileItem[]>('/storage/recent'),
  getActivityLogs: (params?: { provider?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.provider) qs.set('provider', params.provider);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    return apiFetch<StorageActivityLog[]>(`/storage/activity?${qs}`);
  },

  // ─── Google Drive ──────────────────────────────────────────────────────────
  google: {
    connect: async () => {
      const data = await apiFetch<{ url: string }>('/storage/google/connect');
      window.location.href = data.url;
    },
    disconnect: () =>
      apiFetch<{ message: string }>('/storage/google/disconnect', { method: 'POST' }),

    listFiles: (folderId = 'root', pageToken?: string, q?: string) => {
      const qs = new URLSearchParams({ folderId });
      if (pageToken) qs.set('pageToken', pageToken);
      if (q) qs.set('q', q);
      return apiFetch<DriveFileList>(`/storage/google/files?${qs}`);
    },

    uploadFile: (file: File, folderId?: string, onProgress?: (n: number) => void) => {
      return new Promise<DriveFileList['files'][0]>((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', file);
        if (folderId) fd.append('folderId', folderId);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE}/storage/google/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(JSON.parse(xhr.responseText)?.error || 'Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(fd);
      });
    },

    downloadFile: (fileId: string) =>
      `${BASE}/storage/google/download/${fileId}?token=${getToken()}`,

    createFolder: (name: string, parentId?: string) =>
      apiFetch<{ id: string; name: string }>('/storage/google/create-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, parentId }),
      }),

    searchFiles: (q: string) => {
      const qs = new URLSearchParams({ q });
      return apiFetch<DriveFileList>(`/storage/google/search?${qs}`);
    },

    createClientFolders: (clientName: string) =>
      apiFetch<ClientFolderResult>('/storage/google/client-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName }),
      }),

    getClientFolder: (clientName: string) => {
      const qs = new URLSearchParams({ clientName });
      return apiFetch<{ id: string; name: string; webViewLink: string } | null>(
        `/storage/google/client-folder?${qs}`
      );
    },

    deleteFile: (fileId: string) =>
      apiFetch<{ message: string }>(`/storage/google/files/${fileId}`, { method: 'DELETE' }),
  },

  // ─── OneDrive ──────────────────────────────────────────────────────────────
  onedrive: {
    connect: async () => {
      const data = await apiFetch<{ url: string }>('/storage/onedrive/connect');
      window.location.href = data.url;
    },
    disconnect: () =>
      apiFetch<{ message: string }>('/storage/onedrive/disconnect', { method: 'POST' }),

    listFiles: (itemId = 'root', q?: string) => {
      const qs = new URLSearchParams({ itemId });
      if (q) qs.set('q', q);
      return apiFetch<OneDriveItemList>(`/storage/onedrive/files?${qs}`);
    },

    uploadFile: (file: File, folderId?: string, onProgress?: (n: number) => void) => {
      return new Promise<OneDriveItemList['value'][0]>((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', file);
        if (folderId) fd.append('folderId', folderId);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE}/storage/onedrive/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(JSON.parse(xhr.responseText)?.error || 'Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(fd);
      });
    },

    downloadFile: (itemId: string) =>
      `${BASE}/storage/onedrive/download/${itemId}?token=${getToken()}`,

    createFolder: (name: string, parentId?: string) =>
      apiFetch<OneDriveItemList['value'][0]>('/storage/onedrive/create-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, parentId }),
      }),

    createClientFolders: (clientName: string) =>
      apiFetch<ClientFolderResult>('/storage/onedrive/client-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName }),
      }),

    syncDocuments: (clientName: string, files: File[]) => {
      const fd = new FormData();
      fd.append('clientName', clientName);
      files.forEach((f) => fd.append('files', f));
      return apiFetch<OneDriveItemList['value']>('/storage/onedrive/sync', {
        method: 'POST',
        body: fd,
      });
    },
  },

  // ─── Zoho WorkDrive ────────────────────────────────────────────────────────
  zoho: {
    connect: async () => {
      const data = await apiFetch<{ url: string }>('/storage/zoho/connect');
      window.location.href = data.url;
    },
    disconnect: () =>
      apiFetch<{ message: string }>('/storage/zoho/disconnect', { method: 'POST' }),

    listFiles: (folderId = 'root', q?: string) => {
      const qs = new URLSearchParams({ folderId });
      if (q) qs.set('q', q);
      return apiFetch<ZohoItemList>(`/storage/zoho/files?${qs}`);
    },

    uploadFile: (file: File, folderId?: string, onProgress?: (n: number) => void) => {
      return new Promise<ZohoItemList['data'][0]>((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', file);
        if (folderId) fd.append('folderId', folderId);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE}/storage/zoho/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(JSON.parse(xhr.responseText)?.error || 'Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(fd);
      });
    },

    downloadFile: (fileId: string) =>
      `${BASE}/storage/zoho/download/${fileId}?token=${getToken()}`,

    createFolder: (name: string, parentId?: string) =>
      apiFetch<{ id: string; attributes: { name: string } }>('/storage/zoho/create-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, parentId }),
      }),

    searchFiles: (q: string) =>
      apiFetch<ZohoItemList>(`/storage/zoho/search?q=${encodeURIComponent(q)}`),

    createClientFolders: (clientName: string) =>
      apiFetch<ClientFolderResult>('/storage/zoho/client-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName }),
      }),

    getClientFolder: (clientName: string) =>
      apiFetch(`/storage/zoho/client-folder?clientName=${encodeURIComponent(clientName)}`),

    deleteFile: (fileId: string) =>
      apiFetch<{ message: string }>(`/storage/zoho/files/${fileId}`, { method: 'DELETE' }),
  },
};
